import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';

const LazyLDContext = createContext(null);

/**
 * Builds LaunchDarkly user context from app user object
 */
const buildLDContext = (user) => {
  if (!user) return undefined;

  return {
    kind: 'user',
    key: user.user_id,
    register_type: user.register_type,
    created_at: user.created_at,
    profile_setup_completed: user.profile_setup_completed,
    products: user.products,
    dob: user.dob,
    employment_status: user.employment_status,
    nationality: user.nationality,
    utm: user.utm,
    country: user.passport_data?.property_country?.label || user.property_finder_country
  };
};

/**
 * LazyLDProvider - Defers LaunchDarkly initialization until explicitly requested.
 * Prevents unnecessary flag evaluations on pages that don't use feature flags.
 */
export function LazyLDProvider({ children }) {
  const [LDProvider, setLDProvider] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [userContext, setUserContext] = useState(null);

  // Use ref to track initialization state (survives re-renders and Strict Mode)
  const initStateRef = useRef({ started: false, promise: null });

  const initializeLaunchDarkly = useCallback(async () => {
    // Check ref to prevent double initialization (handles Strict Mode)
    if (initStateRef.current.started) {
      return initStateRef.current.promise ?? Promise.resolve();
    }

    // Mark as started immediately (synchronously)
    initStateRef.current.started = true;
    setIsInitializing(true);

    initStateRef.current.promise = asyncWithLDProvider({
      clientSideID: process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID,
      context: buildLDContext(userContext)
    })
      .then((Provider) => {
        setLDProvider(() => Provider);
        setIsInitialized(true);
        setIsInitializing(false);
      })
      .catch((error) => {
        console.error('LaunchDarkly initialization failed:', error);
        setIsInitializing(false);
        // Reset on error to allow retry
        initStateRef.current.started = false;
        initStateRef.current.promise = null;
      });

    return initStateRef.current.promise;
  }, [userContext]);

  const value = useMemo(
    () => ({
      isInitialized,
      isInitializing,
      initializeLaunchDarkly,
      setUserContext,
      userContext
    }),
    [isInitialized, isInitializing, initializeLaunchDarkly, userContext]
  );

  const content = LDProvider ? <LDProvider>{children}</LDProvider> : children;

  return <LazyLDContext.Provider value={value}>{content}</LazyLDContext.Provider>;
}

/**
 * Hook to access lazy LaunchDarkly initialization controls
 */
export function useLazyLaunchDarkly() {
  const context = useContext(LazyLDContext);
  if (!context) {
    throw new Error('useLazyLaunchDarkly must be used within LazyLDProvider');
  }
  return context;
}

export { buildLDContext };
